import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../App';
import { toast } from 'sonner';
import { useListStatePersistence } from '@/hooks/useListStatePersistence';
import {
  Home,
  Search,
  Filter,
  MapPin,
  Bed,
  Bath,
  Maximize,
  Heart,
  Plus,
  ChevronRight,
  Loader2,
  Building,
  DollarSign,
  Globe,
  GraduationCap,
  Shield
} from 'lucide-react';

const API = ''; // Use relative URL for same-origin API calls

const HousingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    property_type: '',
    listing_type: '',
    city: '',
    country: '',
    bedrooms: '',
    max_price: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [categories, setCategories] = useState({
    property_types: [],
    listing_types: [],
    amenities: []
  });
  const [total, setTotal] = useState(0);
  const headerRef = useRef(null);

  // Cache list state across route navigation so back from a listing detail
  // lands the user instantly on the same list at the page header.
  useListStatePersistence({
    key: 'housing',
    state: { properties, total, searchTerm, filters },
    enabled: !loading && properties.length > 0,
    onRestore: (cached) => {
      if (Array.isArray(cached.properties)) setProperties(cached.properties);
      if (typeof cached.total === 'number') setTotal(cached.total);
      if (typeof cached.searchTerm === 'string') setSearchTerm(cached.searchTerm);
      if (cached.filters) setFilters(cached.filters);
      setLoading(false);
      requestAnimationFrame(() => {
        headerRef.current?.scrollIntoView({ block: 'start', behavior: 'instant' });
      });
    },
  });

  useEffect(() => {
    fetchCategories();
    fetchProperties();
  }, [filters]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/api/housing/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.property_type) params.append('property_type', filters.property_type);
      if (filters.listing_type) params.append('listing_type', filters.listing_type);
      if (filters.city) params.append('city', filters.city);
      if (filters.country) params.append('country', filters.country);
      if (filters.bedrooms) params.append('bedrooms', filters.bedrooms);
      if (filters.max_price) params.append('max_price', filters.max_price);

      const res = await axios.get(`${API}/api/housing?${params.toString()}`);
      setProperties(res.data.properties || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProperties();
  };

  const formatPrice = (price, currency, period, listingType) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0
    }).format(price);
    
    if (listingType === 'sale') return formatted;
    return `${formatted}/${period || 'month'}`;
  };

  const getListingTypeColor = (type) => {
    switch (type) {
      case 'rent': return 'bg-blue-100 text-blue-700';
      case 'sale': return 'bg-green-100 text-green-700';
      case 'short-term': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-primary/5 via-accent/5 to-primary/10 py-12 md:py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Home className="w-4 h-4" />
                Find Your New Home Abroad
              </div>
              <h1 className="text-4xl md:text-5xl font-black mb-4" ref={headerRef}>
                Expat Housing & <span className="text-primary">Accommodations</span>
              </h1>
              <p className="text-muted-foreground text-lg mb-8">
                Find expat-friendly apartments, houses, and rooms in cities around the world.
              </p>

              {/* Search Bar */}
              <form onSubmit={handleSearch} className="bg-white rounded-2xl p-4 shadow-lg border-2 border-border">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder="Search by location, neighborhood..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-12 h-12 text-lg rounded-xl border-2"
                      data-testid="housing-search-input"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 px-6 rounded-xl border-2"
                    onClick={() => setShowFilters(!showFilters)}
                  >
                    <Filter className="w-5 h-5 mr-2" />
                    Filters
                  </Button>
                  <Button type="submit" className="h-12 px-8 rounded-xl font-bold" data-testid="housing-search-btn">
                    Search
                  </Button>
                </div>

                {/* Expanded Filters */}
                {showFilters && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-4 pt-4 border-t">
                    <Select
                      value={filters.listing_type}
                      onValueChange={(value) => setFilters({ ...filters, listing_type: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Listing Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Types</SelectItem>
                        <SelectItem value="rent">For Rent</SelectItem>
                        <SelectItem value="sale">For Sale</SelectItem>
                        <SelectItem value="short-term">Short-term</SelectItem>
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.property_type}
                      onValueChange={(value) => setFilters({ ...filters, property_type: value === 'all' ? '' : value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Property Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Properties</SelectItem>
                        {categories.property_types?.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Select
                      value={filters.bedrooms}
                      onValueChange={(value) => setFilters({ ...filters, bedrooms: value === 'any' ? '' : value })}
                    >
                      <SelectTrigger className="rounded-lg">
                        <SelectValue placeholder="Bedrooms" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        <SelectItem value="0">Studio</SelectItem>
                        <SelectItem value="1">1 Bedroom</SelectItem>
                        <SelectItem value="2">2 Bedrooms</SelectItem>
                        <SelectItem value="3">3 Bedrooms</SelectItem>
                        <SelectItem value="4">4+ Bedrooms</SelectItem>
                      </SelectContent>
                    </Select>

                    <Input
                      type="text"
                      placeholder="City"
                      value={filters.city}
                      onChange={(e) => setFilters({ ...filters, city: e.target.value })}
                      className="rounded-lg"
                    />

                    <Input
                      type="text"
                      placeholder="Country"
                      value={filters.country}
                      onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                      className="rounded-lg"
                    />

                    <Input
                      type="number"
                      placeholder="Max Price"
                      value={filters.max_price}
                      onChange={(e) => setFilters({ ...filters, max_price: e.target.value })}
                      className="rounded-lg"
                    />
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h2 className="text-2xl font-bold">{total} Properties Available</h2>
              <p className="text-muted-foreground">Expat-friendly housing options</p>
            </div>
            {user ? (
              <Link to="/housing/create">
                <Button className="font-bold rounded-xl" data-testid="list-property-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  List Your Property
                </Button>
              </Link>
            ) : (
              <Link to="/">
                <Button className="font-bold rounded-xl">
                  <Plus className="w-5 h-5 mr-2" />
                  Sign In to List
                </Button>
              </Link>
            )}
          </div>

          {/* Property Grid */}
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : properties.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
              <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Properties Found</h3>
              <p className="text-muted-foreground mb-6">
                {searchTerm || Object.values(filters).some(v => v)
                  ? 'Try adjusting your search filters'
                  : 'Be the first to list a property!'}
              </p>
              {user && (
                <Link to="/housing/create">
                  <Button className="rounded-xl font-bold">
                    <Plus className="w-5 h-5 mr-2" />
                    List Your Property
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {properties.map(property => (
                <Link 
                  key={property.property_id} 
                  to={`/housing/${property.property_id}`}
                  className="group"
                  data-testid={`property-card-${property.property_id}`}
                >
                  <div className="bg-white rounded-2xl border-2 border-border overflow-hidden hover:border-primary/50 hover:shadow-lg transition-all">
                    {/* Image */}
                    <div className="relative h-48 bg-gradient-to-br from-primary/20 to-accent/20">
                      {property.images?.[0] ? (
                        <img 
                          src={property.images[0]} 
                          alt={property.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Building className="w-16 h-16 text-primary/40" />
                        </div>
                      )}
                      {/* Badges */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getListingTypeColor(property.listing_type)}`}>
                          {property.listing_type === 'short-term' ? 'Short-term' : property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
                        </span>
                        {property.is_expat_friendly && (
                          <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                            Expat Friendly
                          </span>
                        )}
                      </div>
                      <button className="absolute top-3 right-3 p-2 bg-white/80 rounded-full hover:bg-white transition-colors">
                        <Heart className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      {/* Price */}
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-2xl font-black text-primary">
                          {formatPrice(property.price, property.price_currency, property.price_period, property.listing_type)}
                        </span>
                      </div>

                      {/* Title & Property Type */}
                      <h3 className="font-bold text-lg mb-1 group-hover:text-primary transition-colors line-clamp-1">
                        {property.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-3">{property.property_type}</p>

                      {/* Location */}
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <MapPin className="w-4 h-4" />
                        <span className="line-clamp-1">
                          {property.district ? `${property.district}, ` : ''}{property.city}, {property.country}
                        </span>
                      </div>

                      {/* Features */}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground border-t pt-3">
                        <div className="flex items-center gap-1">
                          <Bed className="w-4 h-4" />
                          <span>{property.bedrooms} bed</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Bath className="w-4 h-4" />
                          <span>{property.bathrooms} bath</span>
                        </div>
                        {property.size_sqm && (
                          <div className="flex items-center gap-1">
                            <Maximize className="w-4 h-4" />
                            <span>{property.size_sqm} m²</span>
                          </div>
                        )}
                      </div>

                      {/* Expat Features */}
                      {(property.near_international_schools || property.near_expat_areas || property.visa_assistance) && (
                        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
                          {property.near_international_schools && (
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full flex items-center gap-1">
                              <GraduationCap className="w-3 h-3" />
                              Int'l Schools
                            </span>
                          )}
                          {property.near_expat_areas && (
                            <span className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full flex items-center gap-1">
                              <Globe className="w-3 h-3" />
                              Expat Area
                            </span>
                          )}
                          {property.visa_assistance && (
                            <span className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              Visa Help
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default HousingPage;
